import { useEffect, useState } from 'react'
import { StyleSheet, View } from 'react-native'
import { Trans, useTranslation } from 'react-i18next'
import { observer } from 'mobx-react-lite'
import { usePathname, RouterEvent, Href, useRouter } from 'zens-router'
import { Button, PopUp, Spacing, Text } from 'zens-storybook'
import { Draft } from 'zens-store'
import { pathnameFromHref } from '@dms/helper'

const styles = StyleSheet.create({
  deleteText: {
    marginTop: Spacing.L,
    marginBottom: Spacing.XL,
  },
  deleteBottom: {
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  popup: {
    maxWidth: 600,
  },
})

interface Props {
  draft: Draft<any>
}

const NavigationPrevent = (props: Props) => {
  const { draft } = props
  const [confirmNavigate, setConfirmNavigate] = useState<Href | false>(false)
  const { t } = useTranslation()
  const router = useRouter()
  const pathname = usePathname()

  useEffect(() => {
    const beforeUnloadHandler = (e: BeforeUnloadEvent) => {
      ;(e || window?.event).returnValue = false
      return false // Gecko + Webkit, Safari, Chrome etc.
    }
    const beforeRouteHandler = (event: RouterEvent) => {
      const newPathname = pathnameFromHref(event.href)
      if (newPathname !== pathname && draft.hasChanges) {
        setConfirmNavigate(event.href)
        event.preventDefault = true
      }
      return false
    }

    if (draft.hasChanges) {
      window?.addEventListener?.('beforeunload', beforeUnloadHandler)
      router.addListener(beforeRouteHandler)
    } else {
      window?.removeEventListener?.('beforeunload', beforeUnloadHandler)
      router.removeListener(beforeRouteHandler)
    }
    return () => {
      window?.removeEventListener?.('beforeunload', beforeUnloadHandler)
      router.removeListener(beforeRouteHandler)
    }
  }, [draft.data, draft.hasChanges])

  const forceNavigate = () => {
    draft.reset()
    setTimeout(() => {
      router.push(confirmNavigate as any)
    })
  }

  const skipNavigate = () => {
    setConfirmNavigate(false)
  }

  return (
    <PopUp visible={!!confirmNavigate} onClose={() => setConfirmNavigate(false)} style={styles.popup}>
      <Text level={2}>
        <Trans>Save your changes?</Trans>
      </Text>
      <Text style={styles.deleteText}>
        <Trans>
          Changes were made to one or more fields in this form. Please decide if you want to keep these changes or not.
        </Trans>
      </Text>
      <View style={styles.deleteBottom}>
        <Button title={t("Don't save changes")} light size="L" onPress={forceNavigate} testID="ConfirmNavigate.Force" />
        <Button title={t('Continue editing')} size="L" onPress={skipNavigate} testID="ConfirmNavigate.Skip" />
      </View>
    </PopUp>
  )
}

export default observer(NavigationPrevent)
